import React, { Fragment, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FaPen, FaPlus, FaSearch, FaTrash, FaImage, FaSpinner } from "react-icons/fa";
import LoadingScreen from '../../components/LoadingScreen';

function BrandsPage() {
    const [brandList, setBrandList] = useState([]);
    const [filteredBrands, setFilteredBrands] = useState([]);
    const [brandData, setBrandData] = useState({ name: '', image: '' });
    const [imageUploading, setImageUploading] = useState(false);
    const [savingBrand, setSavingBrand] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [loadedImages, setLoadedImages] = useState({});
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';
    const token = localStorage.getItem('authToken');

    useEffect(() => {
        handleBrandList();
    }, []);

    // Fetch all brands
    const handleBrandList = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/branddata`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const brands = response.data || [];
            setBrandList(brands);
            setFilteredBrands(brands);
        } catch (error) {
            alert("Failed to fetch brands. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Search brand with debouncing
    const handleSearchBrand = (event) => {
        const search = event.target.value.toLowerCase();
        setSearchTerm(search);
        
        if (search) {
            const filtered = brandList.filter(brand => 
                brand.name.toLowerCase().includes(search)
            );
            setFilteredBrands(filtered);
        } else {
            setFilteredBrands(brandList);
        }
    };

    // Handle image upload
    const handleImageUpload = async (event) => {
        try {
            setImageUploading(true);
            const formData = new FormData();
            formData.append('image', event.target.files[0]);
            const response = await axios.post(`${API_URL}/upload-prod-img`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrandData({ ...brandData, image: response.data.path });
            alert("Image uploaded successfully!");
        } catch (error) {
            alert("Image upload failed. Please try again.");
        } finally {
            setImageUploading(false);
        }
    };

    // Handle form input changes
    const handleChange = (event) => {
        setBrandData({ ...brandData, [event.target.name]: event.target.value });
    };

    // Validate input fields
    const validateFields = () => {
        if (!brandData.name.trim()) {
            alert("Brand name is required.");
            return false;
        }
        if (!brandData.image.trim()) {
            alert("Brand image is required.");
            return false;
        }
        return true;
    };

    // Add a new brand
    const handleAddBrand = async () => {
        if (!validateFields()) return;

        try {
            setSavingBrand(true);
            const response = await axios.post(`${API_URL}/add-brand`, brandData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            handleBrandList();
            setBrandData({ name: '', image: '' });
            // Close modal using data-bs-dismiss attribute
            const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
            if (closeButton) {
                closeButton.click();
            }
        } catch (error) {
            alert("Failed to add brand. Please try again.");
        } finally {
            setSavingBrand(false);
        }
    };

    // Delete a brand
    const handleDeleteBrand = async (id) => {
        if (!window.confirm("Are you sure you want to delete this brand?")) return;

        try {
            await axios.delete(`${API_URL}/delete-brand/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert("Brand deleted successfully!");
            handleBrandList();
        } catch (error) {
            alert("Failed to delete brand. Please try again.");
        }
    };

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">BRAND MANAGEMENT</h4>
                                <p className="text-muted p-0 m-0">Manage your product brands and categories</p>
                            </div>
                            <button
                                type="button"
                                className="add-brand-btn"
                                data-bs-toggle="modal"
                                data-bs-target="#exampleModal"
                                aria-label="Add a new brand"
                            >
                                <FaPlus className="icon" />
                                <span className="txt">Add Brand</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="pending-control-panel brands-control-panel">
                    <div className="search-control-wrapper flex-grow-1" style={{ maxWidth: '450px' }}>
                        <input
                            type="text"
                            className="form-control search-input-custom w-100"
                            placeholder="Search by brand name..."
                            value={searchTerm}
                            onChange={handleSearchBrand}
                        />
                        <FaSearch className="search-icon-custom" />
                    </div>
                    <div className="d-flex align-items-center">
                        <small className="text-muted fw-semibold">
                            Showing {filteredBrands.length} of {brandList.length} brands
                        </small>
                    </div>
                </div>

                {loading ? (
                    <LoadingScreen />
                ) : (
                    <div className="brands-grid-row">
                        {filteredBrands.length > 0 ? (
                            filteredBrands.map((data) => (
                                <div className="col-lg-3 col-md-4 col-sm-6" key={data._id}>
                                    <div className="brand-card h-100" tabIndex={0} role="group" aria-label={`Brand ${data.name}`}
                                         onKeyDown={(e)=> { if(e.key==='Enter'){ window.location.href=`/admin/brands/edit/${data._id}`; }}}>
                                        <div className="brand-thumb">
                                            {data.image ? (
                                                <img
                                                    src={data.image.startsWith('http') ? data.image : `${API_URL}${data.image}`}
                                                    alt={data.name}
                                                    loading="lazy"
                                                    className={`brand-img ${loadedImages[data._id] ? 'show' : ''}`}
                                                    onLoad={()=> setLoadedImages(prev=>({...prev,[data._id]:true}))}
                                                    onError={(e)=> { e.target.style.display='none'; }}
                                                />
                                            ) : (
                                                <div className="brand-initial" aria-hidden="true">{data.name.charAt(0).toUpperCase()}</div>
                                            )}
                                            {!loadedImages[data._id] && data.image && <div className="brand-skeleton" />}
                                            <div className="brand-actions-bar">
                                                <Link to={`/admin/brands/edit/${data._id}`} className="brand-action-btn edit" title="Edit Brand" aria-label={`Edit ${data.name}`}>
                                                    <FaPen />
                                                </Link>
                                                <button type="button" className="brand-action-btn delete" title="Delete Brand" aria-label={`Delete ${data.name}`} onClick={()=>handleDeleteBrand(data._id)}>
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="brand-body">
                                            <h6 className="brand-name" title={data.name}>{data.name}</h6>
                                            <small className="text-muted">Brand</small>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center py-5">
                                        <FaImage className="text-muted fs-1 mb-3" />
                                        <h5 className="text-muted">
                                            {searchTerm ? 'No brands found matching your search.' : 'No brands found'}
                                        </h5>
                                        <p className="text-muted">
                                            {searchTerm ? 'Try adjusting your search terms.' : 'Add your first brand to get started.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Add Brand Modal */}
            <div className="modal fade add-brand-modal" id="exampleModal" tabIndex={-1} aria-labelledby="exampleModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold">Add New Brand</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div className="modal-body pt-0">
                            <div className="mb-3">
                                <label className="form-label fw-bold">Brand Name</label>
                                <input 
                                    type="text" 
                                    className="form-control rounded-0" 
                                    placeholder="Enter brand name" 
                                    name="name" 
                                    value={brandData.name} 
                                    onChange={handleChange} 
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold">Brand Image</label>
                                <input 
                                    type="file" 
                                    className="form-control rounded-0" 
                                    accept="image/*" 
                                    onChange={handleImageUpload} 
                                />
                                {imageUploading && (
                                    <div className="mt-2">
                                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                        <small className="text-muted">Uploading image...</small>
                                    </div>
                                )}
                                {brandData.image && (
                                    <div className="mt-2 add-brand-preview border">
                                        <div className="d-flex align-items-center justify-content-between px-2 py-1 bg-white border-bottom">
                                            <small className="text-success d-flex align-items-center m-0"><FaImage className="me-1" /> Preview</small>
                                            <small className="text-muted">{brandData.name || 'New brand'}</small>
                                        </div>
                                        <div className="p-2 text-center bg-light">
                                            <img src={brandData.image.startsWith('http') ? brandData.image : `${API_URL}${brandData.image}`} alt="Preview" className="img-fluid" style={{maxHeight:'140px',objectFit:'contain'}} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer pt-0">
                            <button type="button" className="btn btn-light rounded-0 border" data-bs-dismiss="modal">
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                className="btn save-brand-btn rounded-0" 
                                onClick={handleAddBrand}
                                disabled={savingBrand || imageUploading || !brandData.name.trim() || !brandData.image.trim()}
                            >
                                {savingBrand ? (
                                    <>
                                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Brand'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Styles moved to global index.css under Brand Management section */}
        </Fragment>
    );
}

export default BrandsPage;
